import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  CategoryPageBelowListEntry,
  CategoryPageSubheaderEntry,
  CustomTagFilterConfig,
  ListPageEntry,
  MenuContext,
  ModuleRegistryEntry,
  RoutablePageRegistryEntry,
  StoreFrontCarouselTitleProps,
  StoreFrontCarouselTitleRegistryEntry,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import type { Dispensaries, RemoteExtensionsConfiguration } from 'src/gql/graphql';
import useCurrentDispensary from 'src/dispensary/hooks/use-current-dispensary';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useLazyRemoteModule from 'src/utils/module-federation/use-lazy-remote-module';
import useUI from 'hooks/use-ui';
import { useRouter } from 'next/compat/router';
import type { CategoryPageBelowListIterable } from 'src/dispensary/category/category-page-below-list';

import { getLivePreviewConfig } from 'src/utils/module-federation/get-live-preview-config';
import { isCheckoutOwnedRoute, isStorefrontCheckoutThemeRoute } from 'src/checkout/theme/checkout-theme-routes';
import { remoteComponentIsValid } from 'src/theme/remote-component-is-valid';

import {
  CommerceComponents,
  ExtendedRemoteModuleRegistry,
  RemoteTheme,
  RouteablePage,
  defaultCommerceComponents,
  CustomSmartTagFilterConfig,
} from './context';
import { useCommerceBridge } from './bridge/bridge-context';

export { remoteComponentIsValid };

// Becomes the CommerceComponentsContext value for the majority of stores, so it must be
// one shared reference — a fresh spread per call re-renders every consumer on unrelated
// churn. Resolved is true because there is no remote to wait for.
/**
 * Validates federated custom tag filter configurations at runtime before passing to consumers.
 * - Value must be an array; otherwise returns [].
 * - Each entry must be a plain object with non-empty string `category`, `name`, `title`
 *   and a non-empty `match` array containing non-empty strings.
 * - Malformed entries are dropped without throwing.
 * - Duplicate categories within the same source: first valid entry wins.
 */
export function validateCustomTagFilterConfigs<T extends CustomSmartTagFilterConfig | CustomTagFilterConfig>(
  raw: unknown
): T[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const seenCategories = new Set<string>();
  const validConfigs: T[] = [];

  raw.forEach((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return;
    }

    const { category, name, title, match } = item as Record<string, unknown>;

    if (
      typeof category !== 'string' ||
      category.trim() === '' ||
      typeof name !== 'string' ||
      name.trim() === '' ||
      typeof title !== 'string' ||
      title.trim() === '' ||
      !Array.isArray(match) ||
      match.length === 0 ||
      !match.every((m) => typeof m === 'string' && m.trim().length > 0)
    ) {
      return;
    }

    if (seenCategories.has(category)) {
      return;
    }

    seenCategories.add(category);
    validConfigs.push({
      category,
      name,
      title,
      match: [...match],
    } as T);
  });

  return validConfigs;
}

const resolvedDefaultCommerceComponents: CommerceComponents = {
  ...defaultCommerceComponents,
  isRemoteThemeResolved: true,
};

type RemoteCompositionProps = {
  component: React.ReactElement;
  renderRemote?: (component: React.ReactElement) => React.ReactElement;
};

function RemoteComposition({ component, renderRemote }: RemoteCompositionProps): React.ReactElement {
  return renderRemote ? renderRemote(component) : component;
}

// Reserved heights mirror the stock fallback blocks (DefaultHeader 72px, desktop nav
// min-height 68px) so held header-block slots keep their footprint while a remote
// theme loads and content below doesn't reflow when the theme mounts.
const STOREFRONT_HEADER_SKELETON_MIN_HEIGHT = 'var(--dcm-storefront-header-height, 72px)';
const STOREFRONT_NAVIGATION_SKELETON_MIN_HEIGHT = 'var(--dcm-header-min-height, 68px)';

const ThemeSlotSkeleton: React.FC<{ minHeight: string }> = ({ minHeight }) => (
  <div data-testid='remote-theme-slot-skeleton' style={{ minHeight }} />
);

export function useRemoteCommerceComponents(): {
  getCommerceComponents: (styledTheme?: RemoteTheme) => CommerceComponents;
  remoteThemeModule: any;
  modulePath: string | undefined;
  isRemoteThemeResolved: boolean;
  shouldProvideCommerceBridge: boolean;
} {
  const [moduleLoadError, setModuleLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const UI = useUI();
  const router = useRouter();
  const { isStoreFront, isKiosk } = UI;
  const isCheckoutOwnedPage = isCheckoutOwnedRoute(router?.route);
  const isStorefrontCheckoutPage = isStorefrontCheckoutThemeRoute(router?.route);
  const isIsolatedPlusCheckoutPage = isCheckoutOwnedPage && !isStorefrontCheckoutPage;
  const isStoreFrontOrKiosk = !isIsolatedPlusCheckoutPage && (isStoreFront || isKiosk);

  const { dispensary: currentDispensary } = useCurrentDispensary();
  const { dispensary: legacyDispensary } = useDispensary() as { dispensary?: Dispensaries | undefined };
  const dispensary = currentDispensary ?? legacyDispensary;
  const rawPath = typeof router?.asPath === 'string' ? router.asPath : '';
  const pathWithoutHash = rawPath.split('#')[0];
  const currentSearchString = pathWithoutHash.includes('?') ? pathWithoutHash.split('?')[1] : undefined;

  const [livePreviewConfig, setLivePreviewConfig] = useState<RemoteExtensionsConfiguration | null>(() =>
    getLivePreviewConfig(currentSearchString)
  );

  useEffect(() => {
    const nextConfig = getLivePreviewConfig(currentSearchString);
    if (nextConfig) {
      setLivePreviewConfig(nextConfig);
    }
  }, [currentSearchString]);

  // Use window.reactEnv as an early source for the theme config. It's server-injected
  // before React boots, so we know immediately whether a remote theme is configured —
  // without waiting for the dispensary Apollo query to complete.
  const reactEnvThemeConfig = useMemo(
    () => (typeof window !== 'undefined' ? window.reactEnv?.remoteExtensions?.theme : null),
    []
  );
  // Once dispensary has loaded, trust only its data. Before it loads, seed from
  // window.reactEnv so we know a remote is coming before the query round-trip.
  const dispensaryOrEarlyTheme = dispensary ? dispensary.remoteExtensions?.theme : reactEnvThemeConfig;
  // Isolated Plus checkout remains fully native. Pro storefront checkout preserves the
  // root theme's pre-existing GlobalStyles only; getCommerceComponents projects every
  // executable storefront slot, event, page, iterable, and theme config back to defaults.
  const customizationProvider = isIsolatedPlusCheckoutPage
    ? null
    : livePreviewConfig ?? (isStoreFrontOrKiosk ? dispensaryOrEarlyTheme : null);

  // Whether a remote theme is configured for this store. This is knowable during SSR: the
  // dispensary config (including remoteExtensions.theme) is prefetched into the Apollo cache
  // server-side, and window.reactEnv.remoteExtensions.theme is injected before React boots on the
  // client. Because both the server render and the first client render derive the same value, we
  // can seed render state from it without risking a hydration mismatch.
  const isThemeExpected = Boolean(customizationProvider?.modulePath);

  // Holds theme component slots blank until the initial remote fetch settles, preventing the stock
  // Dutchie UI from painting before the customer theme arrives. Module federation is browser-only,
  // so the remote can never resolve during SSR; seeding from isThemeExpected makes the server itself
  // hold the slot (rather than flashing stock UI that the client would immediately replace), and
  // keeps the first client render identical to the server HTML for clean hydration. The client then
  // swaps in the real remote once loadRemote resolves post-mount.
  const [isPendingInitialLoad, setIsPendingInitialLoad] = useState<boolean>(isThemeExpected);

  const isServer = typeof window === 'undefined';
  const skip = isServer || !customizationProvider?.modulePath;

  // If skip transitions to true after mount (e.g. dispensary loaded with no theme config),
  // release the hold so stock/fallback components can render.
  useEffect(() => {
    if (skip) {
      setIsPendingInitialLoad(false);
    }
  }, [skip]);

  const handleLoadFail = useCallback((error: string) => {
    setModuleLoadError(error);
    setIsPendingInitialLoad(false);
  }, []);

  const handleLoading = useCallback((loading: boolean) => {
    if (loading) {
      // Clear any previous error so a successful re-fetch can render the theme.
      setModuleLoadError(null);
    }
    setIsLoading(loading);
    if (!loading) {
      setIsPendingInitialLoad(false);
    }
  }, []);

  const remoteThemeModule = useLazyRemoteModule<{
    default?: ExtendedRemoteModuleRegistry;
  }>({
    manifestUrl: customizationProvider?.manifestUrl ?? '',
    modulePath: customizationProvider?.modulePath ?? '',
    scope: customizationProvider?.scope ?? '',
    fallback: { default: undefined },
    skip,
    onLoadFail: handleLoadFail,
    onLoading: handleLoading,
  });

  const wrapComponentFactory = (
    componentEntry?: ModuleRegistryEntry,
    styledTheme: RemoteTheme = null,
    skeletonMinHeight?: string,
    exposeCommerceBridge = true
  ): ((o?: {
    fallback?: React.FC;
    renderRemote?: (component: React.ReactElement) => React.ReactElement;
  }) => React.FC) => {
    if (componentEntry && typeof componentEntry !== 'function') {
      let context: MenuContext | null = null;
      if (isKiosk) {
        context = 'kiosk';
      } else if (isStoreFront) {
        context = 'store-front';
      }
      if (!context) {
        componentEntry = undefined;
      } else {
        componentEntry = componentEntry[context];
      }
    }

    const Component = componentEntry;
    const RemoteComponent = Component as React.FC<any>;

    type WrappedComponentProps = Record<string, unknown> & {
      __remoteFallback: React.FC;
      renderRemoteComponent?: (component: React.ReactElement) => React.ReactElement;
    };

    const RemoteComponentBoundary: React.FC<WrappedComponentProps & { remoteComponent: React.ReactElement }> = ({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __remoteFallback: FallbackComponent,
      remoteComponent,
      renderRemoteComponent: renderRemote,
      ...props
    }) => (
      <ErrorBoundary
        fallbackRender={({ error }) => {
          console.error(`Remote Component error (${String(Component?.name)}):`, error);
          return <FallbackComponent {...props} />;
        }}
      >
        <RemoteComposition component={remoteComponent} renderRemote={renderRemote} />
      </ErrorBoundary>
    );

    const BridgedWrappedComponent: React.FC<WrappedComponentProps> = ({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __remoteFallback: FallbackComponent,
      renderRemoteComponent,
      ...props
    }) => {
      const remoteData = useCommerceBridge();

      if (!Component || remoteData === null) {
        return <FallbackComponent {...props} />;
      }
      if (remoteData === undefined) {
        return null;
      }

      const remoteComponent = (
        <RemoteComponent theme={styledTheme} __dsdk__dataBridgeData={remoteData} data={remoteData} {...props} />
      );
      return (
        <RemoteComponentBoundary
          __remoteFallback={FallbackComponent}
          remoteComponent={remoteComponent}
          renderRemoteComponent={renderRemoteComponent}
          {...props}
        />
      );
    };

    const BridgeFreeWrappedComponent: React.FC<WrappedComponentProps> = ({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __remoteFallback: FallbackComponent,
      renderRemoteComponent,
      ...props
    }) => {
      if (!Component) {
        return <FallbackComponent {...props} />;
      }

      return (
        <RemoteComponentBoundary
          __remoteFallback={FallbackComponent}
          remoteComponent={<RemoteComponent theme={styledTheme} {...props} />}
          renderRemoteComponent={renderRemoteComponent}
          {...props}
        />
      );
    };

    const WrappedComponent = exposeCommerceBridge ? BridgedWrappedComponent : BridgeFreeWrappedComponent;

    return ({
      fallback = () => <></>,
      renderRemote,
    }: {
      fallback?: React.FC;
      renderRemote?: (component: React.ReactElement) => React.ReactElement;
    } = {}) => {
      // If there was an error loading the module, return the fallback
      if (moduleLoadError) {
        return fallback;
      }

      // Hold the slot while the initial remote fetch (or a reload) is in flight so stock
      // Dutchie components don't flash before the theme arrives. Reserved slots keep their
      // height instead of collapsing to zero so content below doesn't reflow on mount.
      if (isLoading || isPendingInitialLoad) {
        if (skeletonMinHeight) {
          return () => <ThemeSlotSkeleton minHeight={skeletonMinHeight} />;
        }
        return () => <></>;
      }

      // If the Component is not defined, return the fallback
      if (!Component) {
        return fallback;
      }

      // If the Component is valid, return it with the fallback bound via props
      if (remoteComponentIsValid(Component)) {
        const BoundComponent: React.FC<any> = (props) => (
          <WrappedComponent __remoteFallback={fallback} renderRemoteComponent={renderRemote} {...props} />
        );
        return BoundComponent;
      }

      // If the component is not valid, return the fallback
      return fallback;
    };
  };

  const findCarouselTitle = (
    entries: Record<string, StoreFrontCarouselTitleRegistryEntry> | undefined,
    carouselId: string,
    styledTheme: RemoteTheme,
    fallback: React.FC<StoreFrontCarouselTitleProps>
  ): React.FC<StoreFrontCarouselTitleProps> => {
    // Carousel-title overrides are optional per carousel. Keep the native title while the
    // registry loads so themes that omit this id never incur a title-sized layout shift.
    if (moduleLoadError || skip || isLoading || isPendingInitialLoad) {
      return fallback;
    }

    if (!entries || !Object.prototype.hasOwnProperty.call(entries, carouselId)) {
      return fallback;
    }

    const entry = (entries as Partial<Record<string, StoreFrontCarouselTitleRegistryEntry>>)[carouselId];
    if (!entry) {
      return fallback;
    }

    return wrapComponentFactory(entry, styledTheme)({ fallback }) as React.FC<StoreFrontCarouselTitleProps>;
  };

  const wrapIterables = (
    iterables?: ModuleRegistryEntry[],
    styledTheme: RemoteTheme = null
  ): (() => React.ElementType[]) => () =>
    iterables?.map((iterable) => wrapComponentFactory(iterable, styledTheme)()) ?? [];

  const wrapCategoryIterables = (
    iterables?: ListPageEntry[],
    styledTheme: RemoteTheme = null
  ): (() => { category: ListPageEntry['category']; components: React.ElementType[] }[]) => () =>
    iterables?.map((iterable) => ({
      category: iterable.category,
      components: iterable.components.map((component) => wrapComponentFactory(component, styledTheme)()),
    })) ?? [];

  const wrapCategoryBelowList = (
    entries?: CategoryPageBelowListEntry[],
    styledTheme: RemoteTheme = null
  ): (() => CategoryPageBelowListIterable[]) => () => {
    if (moduleLoadError || skip || isPendingInitialLoad || !Array.isArray(entries)) {
      return [];
    }
    return entries
      .filter((entry): entry is CategoryPageBelowListEntry =>
        Boolean(
          entry &&
            (Boolean(entry.category) || Boolean(entry.subcategory) || Boolean(entry.collection)) &&
            Array.isArray(entry.components)
        )
      )
      .map((entry) => ({
        category: entry.category,
        subcategory: entry.subcategory,
        collection: entry.collection,
        components: entry.components.map((component) => wrapComponentFactory(component, styledTheme)()),
      }));
  };

  // Not a component registry -- returns the raw, unvalidated SDK-authored
  // entries for `CategoryPageSubheaders` as-is. Matching/validation happens
  // in `findMatchingCategorySubheaderText` (category-page-subheaders.ts),
  // called by the host consumer, so no remote-module-loading concerns
  // (skip/error/pending state) leak into that pure helper.
  const getCategorySubheaders = (
    entries?: CategoryPageSubheaderEntry[]
  ): (() => CategoryPageSubheaderEntry[]) => () => {
    if (moduleLoadError || skip || isPendingInitialLoad) {
      return [];
    }
    return entries ?? [];
  };

  const wrapPages = (
    pages: RoutablePageRegistryEntry[] = [],
    styledTheme: RemoteTheme = null,
    fallback: React.FC = () => <></>
  ): RouteablePage[] =>
    pages.map((page) => ({
      ...page,
      component: ({ fallback: pageFallback = fallback } = {}) =>
        wrapComponentFactory(page.component, styledTheme)({ fallback: pageFallback }),
    }));

  const findPage = (
    pagesContainer,
    path = '',
    fallback: React.FC = () => <></>,
    styledTheme: RemoteTheme = null
  ): React.FC => {
    // If there was an error loading the module, return the fallback
    // return fallback on skip, this implies we don't have a config for this
    if (moduleLoadError || skip) {
      return fallback;
    }

    // Hold pages blank while the initial remote fetch is in flight.
    if (isPendingInitialLoad || !pagesContainer) {
      return () => <></>;
    }

    const page = pagesContainer.find((p) => p.path === path);

    if (!page) {
      return fallback;
    }

    return wrapComponentFactory(page.component, styledTheme)({ fallback });
  };

  const isRemoteThemeResolved = !isPendingInitialLoad && !isLoading;

  const exposedRemoteThemeModule = useMemo(() => {
    if (isIsolatedPlusCheckoutPage) {
      return { default: undefined };
    }

    if (isStorefrontCheckoutPage) {
      const GlobalStyles = remoteThemeModule.default?.GlobalStyles;
      return { default: GlobalStyles ? { GlobalStyles } : undefined };
    }

    return remoteThemeModule;
  }, [isIsolatedPlusCheckoutPage, isStorefrontCheckoutPage, remoteThemeModule]);

  const getCommerceComponents = useCallback(
    (styledTheme: RemoteTheme = null): CommerceComponents => {
      if (!customizationProvider) {
        return resolvedDefaultCommerceComponents;
      }

      if (isStorefrontCheckoutPage) {
        return {
          ...resolvedDefaultCommerceComponents,
          components: {
            ...resolvedDefaultCommerceComponents.components,
            // Preserve pre-existing root-theme styling on Pro checkout without handing
            // GlobalStyles the commerce bridge or exposing any generic registry behavior.
            GlobalStyles: wrapComponentFactory(remoteThemeModule.default?.GlobalStyles, styledTheme, undefined, false),
          },
          events: undefined,
          isRemoteThemeResolved,
        };
      }

      return {
        components: {
          StoreFrontHeader: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontHeader,
            styledTheme,
            STOREFRONT_HEADER_SKELETON_MIN_HEIGHT
          ),
          StoreFrontMeta: wrapComponentFactory(remoteThemeModule.default?.StoreFrontMeta, styledTheme),
          StoreFrontNavigation: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontNavigation,
            styledTheme,
            STOREFRONT_NAVIGATION_SKELETON_MIN_HEIGHT
          ),
          StoreFrontFooter: wrapComponentFactory(remoteThemeModule.default?.StoreFrontFooter, styledTheme),
          GlobalStyles: wrapComponentFactory(remoteThemeModule.default?.GlobalStyles, styledTheme),
          StoreFrontHero: wrapComponentFactory(remoteThemeModule.default?.StoreFrontHero, styledTheme),
          StoreFrontLoyaltyWidget: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontLoyaltyWidget,
            styledTheme
          ),
          StoreFrontLoginWidget: wrapComponentFactory(remoteThemeModule.default?.StoreFrontLoginWidget, styledTheme),
          StoreFrontCategoryTiles: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontCategoryTiles,
            styledTheme
          ),
          StoreFrontSpecialsCategoryTiles: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontSpecialsCategoryTiles,
            styledTheme
          ),
          ListPageHero: wrapComponentFactory(remoteThemeModule.default?.ListPageHero, styledTheme),
          ProductDetailsPrimary: wrapComponentFactory(remoteThemeModule.default?.ProductDetailsPrimary, styledTheme),
          ProductDetailsMeta: wrapComponentFactory(remoteThemeModule.default?.ProductDetailsMeta, styledTheme),
          ProductCard: wrapComponentFactory(remoteThemeModule.default?.ProductCard, styledTheme),
          ProductCardListItem: wrapComponentFactory(remoteThemeModule.default?.ProductCardListItem, styledTheme),
          FilterSidePanel: wrapComponentFactory(remoteThemeModule.default?.FilterSidePanel, styledTheme),
          SpecialsPageHeader: wrapComponentFactory(remoteThemeModule.default?.SpecialsPageHeader, styledTheme),
          SpecialsCard: wrapComponentFactory(remoteThemeModule.default?.SpecialsCard, styledTheme),
        },
        findCarouselTitle: ({ carouselId, fallback = () => <></> }) =>
          findCarouselTitle(remoteThemeModule.default?.StoreFrontCarouselTitles, carouselId, styledTheme, fallback),
        findPage: ({ path, fallback = () => <></> }) =>
          findPage(remoteThemeModule.default?.RouteablePages, path, fallback, styledTheme),
        iterables: {
          StoreFrontCarouselInterstitials: wrapIterables(
            remoteThemeModule.default?.StoreFrontCarouselInterstitials,
            styledTheme
          ),
          CategoryPageInterstitials: wrapCategoryIterables(
            remoteThemeModule.default?.CategoryPageInterstitials,
            styledTheme
          ),
          CategoryPageSlots: wrapCategoryIterables(remoteThemeModule.default?.CategoryPageSlots, styledTheme),
          CategoryPageBelowList: wrapCategoryBelowList(remoteThemeModule.default?.CategoryPageBelowList, styledTheme),
          CategoryPageSubheaders: getCategorySubheaders(remoteThemeModule.default?.CategoryPageSubheaders),
        },
        pages: ({ fallback = () => <></> } = {}) =>
          wrapPages(remoteThemeModule.default?.RouteablePages, styledTheme, fallback),
        events: remoteThemeModule.default?.events,
        isRemoteThemeResolved,
        themeConfig: {
          customTagFilters: validateCustomTagFilterConfigs<CustomTagFilterConfig>(
            remoteThemeModule.default?.enableCustomTagFilters ?? []
          ),
          customSmartTagFilters: validateCustomTagFilterConfigs<CustomSmartTagFilterConfig>(
            remoteThemeModule.default?.enableCustomSmartTagFilters ?? []
          ),
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remoteThemeModule, customizationProvider, isRemoteThemeResolved, isStorefrontCheckoutPage]
  );
  return {
    getCommerceComponents,
    remoteThemeModule: exposedRemoteThemeModule,
    modulePath: customizationProvider?.modulePath ?? '',
    isRemoteThemeResolved,
    shouldProvideCommerceBridge: Boolean(customizationProvider?.modulePath) && !isStorefrontCheckoutPage,
  };
}
